import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { Box } from 'seagull';
import { LottiePlayer } from '../../LottiePlayer/LottiePlayer';
import giftAnimation from './gift.json';
import styles from './VideoPlayer.module.scss';
const VIDEO_ID = 'zbXQwOdZYyk';
const YT_ORIGINS = [
    'https://www.youtube.com',
    'https://www.youtube-nocookie.com'
];
const sendYtCommand = (iframe, func)=>{
    if ((iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow) == null) return;
    iframe.contentWindow.postMessage(JSON.stringify({
        event: 'command',
        func,
        args: []
    }), '*');
};
export const VideoPlayer = (param)=>{
    let { progress, onPlay, onPause, onComplete, videoAlt, isMobile } = param;
    const iframeRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isHovering, setIsHovering] = useState(false);
    const handleIframeLoad = useCallback(()=>{
        const iframe = iframeRef.current;
        if ((iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow) == null) return;
        iframe.contentWindow.postMessage(JSON.stringify({
            event: 'listening'
        }), '*');
    }, []);
    useEffect(()=>{
        const onMessage = (event)=>{
            if (!YT_ORIGINS.includes(event.origin)) return;
            try {
                var _data_info;
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                const playerState = data === null || data === void 0 ? void 0 : (_data_info = data.info) === null || _data_info === void 0 ? void 0 : _data_info.playerState;
                if (playerState === undefined) return;
                if (playerState === 1 || playerState === 3) {
                    setIsPlaying(true);
                    onPlay();
                } else if (playerState === 2) {
                    setIsPlaying(false);
                    onPause();
                }
            } catch (unused) {
            // ignore non-JSON messages
            }
        };
        window.addEventListener('message', onMessage);
        return ()=>window.removeEventListener('message', onMessage);
    }, [
        onPlay,
        onPause
    ]);
    const togglePlay = useCallback(()=>{
        const next = !isPlaying;
        setIsPlaying(next);
        sendYtCommand(iframeRef.current, next ? 'playVideo' : 'pauseVideo');
    }, [
        isPlaying
    ]);
    useEffect(()=>{
        if (progress < 100) return;
        const timer = setTimeout(onComplete, 1500);
        return ()=>clearTimeout(timer);
    }, [
        progress,
        onComplete
    ]);
    const showOverlayButton = !isPlaying || isHovering;
    return /*#__PURE__*/ _jsxs(motion.div, {
        className: clsx(styles.root, isMobile && styles.mobile),
        initial: {
            opacity: 0
        },
        animate: {
            opacity: 1
        },
        exit: {
            opacity: 0
        },
        transition: {
            duration: 0.4
        },
        children: [
            /*#__PURE__*/ _jsx(Box, {
                className: styles.videoWrapper,
                children: /*#__PURE__*/ _jsxs(Box, {
                    className: styles.videoContainer,
                    onMouseEnter: ()=>setIsHovering(true),
                    onMouseLeave: ()=>setIsHovering(false),
                    children: [
                        /*#__PURE__*/ _jsx("iframe", {
                            ref: iframeRef,
                            className: styles.iframe,
                            src: "https://www.youtube-nocookie.com/embed/".concat(VIDEO_ID, "?autoplay=1&controls=0&modestbranding=1&rel=0&showinfo=0&enablejsapi=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&playsinline=1&origin=").concat(window.location.origin),
                            title: videoAlt,
                            allow: "autoplay; encrypted-media",
                            onLoad: handleIframeLoad
                        }),
                        /*#__PURE__*/ _jsx(Box, {
                            className: styles.clickBlocker,
                            onClick: togglePlay
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: clsx(styles.overlayButton, showOverlayButton && styles.visible),
                            onClick: togglePlay,
                            "aria-label": isPlaying ? 'Pause' : 'Play',
                            children: isPlaying ? /*#__PURE__*/ _jsx("span", {
                                className: styles.pauseIcon
                            }) : /*#__PURE__*/ _jsx("span", {
                                className: styles.playIcon
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(motion.div, {
                className: styles.controls,
                initial: {
                    opacity: 0
                },
                animate: {
                    opacity: 1
                },
                transition: {
                    delay: 0.3,
                    duration: 0.4
                },
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.progressTrack,
                        children: /*#__PURE__*/ _jsx(Box, {
                            className: styles.progressFill,
                            style: {
                                width: "".concat(progress, "%")
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.giftIcon,
                        children: /*#__PURE__*/ _jsx(LottiePlayer, {
                            animationData: giftAnimation,
                            autoplay: progress >= 100,
                            loop: false
                        })
                    })
                ]
            })
        ]
    }, "playing");
};
